import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth0 } from '../../hooks/useAuth0';
import { useLocation } from 'react-router-dom';
import api, { apiService } from '../../services/api';
import { useSubscription } from '../../hooks/useSubscription';
import SourcesPanel from '../SourcesPanel';
import AIDisclosureModal, { hasAcceptedAIDisclosure, recordAIDisclosureAcceptance } from './AIDisclosureModal';

const FREE_LIMIT = 3;
const STORAGE_KEY = 'copilot_session_count';
const STORAGE_DATE = 'copilot_session_date';

const PREMIUM_ENABLED = process.env.REACT_APP_ENABLE_PREMIUM === 'true';

// ── HIDE ON THESE ROUTES ─────────────────────────────────────────────────────
const HIDDEN_ROUTES = ['/my-health/care-copilot'];

// ── Context Definitions (shown in fullscreen sidebar) ────────────────────────
const CONTEXTS = [
  { id:'general',     icon:'🧬', label:'CKD',            tooltip:'General CKD Questions',           color:'#2d9de8', systemNote:'I am now focused on general CKD questions.', systemPrompt:'You are Care Copilot, an AI health companion specialized in chronic kidney disease (CKD). Answer questions about diabetic nephropathy, hypertensive nephrosclerosis, glomerulonephritis, and polycystic kidney disease — symptoms, diagnosis, CKD stage, complications, and treatment options — in plain, compassionate language suitable for patients and caregivers.' },
  { id:'labs',        icon:'🧪', label:'Lab Results',    tooltip:'Discuss My Lab Results',          color:'#0ab5a0', systemNote:'I am now in Lab Results mode. Share your lab values and I will explain what they mean.', systemPrompt:'You are Care Copilot in Lab Results mode. Help patients understand CKD-related lab values — eGFR, UACR, potassium, PTH, hemoglobin, phosphorus. Explain values in plain English with clinical significance.' },
  { id:'diagnosis',   icon:'🩺', label:'My Diagnosis',  tooltip:"Discuss Doctor's Diagnosis",      color:'#5b3fa6', systemNote:"I am now in Diagnosis Discussion mode. Share what your doctor told you and I'll help you understand it.", systemPrompt:"You are Care Copilot in Diagnosis Discussion mode. Help patients understand their CKD diagnosis — etiology, CKD stage, complications, and treatment path in plain language." },
  { id:'trials',      icon:'🎯', label:'Trials',         tooltip:'Understand Clinical Trials',      color:'#e8a020', systemNote:'I am now in Clinical Trials mode. Ask me anything about finding or enrolling in CKD trials.', systemPrompt:'You are Care Copilot in Clinical Trials mode. Help patients understand clinical trials for CKD — how to find them, eligibility, what participation involves, and how to talk to their care team.' },
  { id:'records',     icon:'📄', label:'Records',        tooltip:'Understand My Medical Records',   color:'#e05c3a', systemNote:'I am now in Medical Records mode. Share details from your records and I will help you understand them.', systemPrompt:'You are Care Copilot in Medical Records mode. Help patients interpret kidney biopsy reports, imaging findings, and discharge summaries related to CKD.' },
  { id:'medications', icon:'💊', label:'Medications',    tooltip:'Medications & Treatment',         color:'#059669', systemNote:'I am now in Medications mode. Ask me about your CKD medications or treatment options.', systemPrompt:'You are Care Copilot in Medications mode. Help patients understand CKD medications — ACE inhibitors, ARBs, SGLT2 inhibitors, finerenone, phosphate binders, tolvaptan. Explain mechanism, dosing, and side effects in plain language.' },
  { id:'doctorvisit', icon:'📋', label:'Doctor Visit',   tooltip:'Prepare for Doctor Visit',        color:'#1a5fa8', systemNote:'I am now in Doctor Visit Prep mode. Tell me about your upcoming appointment and I will help you prepare.', systemPrompt:'You are Care Copilot in Doctor Visit Preparation mode. Help CKD patients prepare for nephrology appointments — generate relevant questions, explain likely tests, and help organize their symptoms.' },
  { id:'type',        icon:'🔬', label:'My Cause',       tooltip:'Understand Your CKD Cause',       color:'#0B2545', systemNote:'I am now in CKD Cause mode. Tell me your cause and I will explain everything specific to your situation.', systemPrompt:'You are Care Copilot in CKD Cause Explanation mode. Help patients deeply understand their specific CKD etiology — diabetic nephropathy, hypertensive nephrosclerosis, glomerulonephritis, polycystic kidney disease, or other/unknown causes.' },
  { id:'profile',     icon:'📝', label:'My Profile',     tooltip:'Complete My Profile',              color:'#f59e0b', systemNote:"I am now in Profile Assistant mode. Let's get your profile filled in!", systemPrompt: null },
  { id:'daily-checkin', icon:'👋', label:'Daily Check-In', tooltip:'Daily Check-In', color:'#0ab5a0', systemNote: null, systemPrompt: null },
];

function getSessionCount() {
  const today = new Date().toDateString();
  if (sessionStorage.getItem(STORAGE_DATE) !== today) {
    sessionStorage.setItem(STORAGE_DATE, today);
    sessionStorage.setItem(STORAGE_KEY, '0');
    return 0;
  }
  return parseInt(sessionStorage.getItem(STORAGE_KEY) || '0');
}

function incrementSessionCount() {
  const count = getSessionCount() + 1;
  sessionStorage.setItem(STORAGE_KEY, count.toString());
  return count;
}

function stripMarkdown(text) {
  return (text || '')
    .replace(/\*\*(.*?)\*\*/g, '$1').replace(/\*(.*?)\*/g, '$1')
    .replace(/<br>/g, ' ').replace(/→/g, '').replace(/✅/g, '')
    .replace(/\n/g, ' ').trim();
}

function renderMarkdown(text) {
  return (text || '')
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/\n/g, '<br>');
}

function useVoice({ muted, onTranscript }) {
  const [speaking, setSpeaking] = useState(false);
  const [listening, setListening] = useState(false);
  const [supported, setSupported] = useState({ tts: false, stt: false });
  const recognitionRef = useRef(null);

  useEffect(() => {
    setSupported({
      tts: 'speechSynthesis' in window,
      stt: 'SpeechRecognition' in window || 'webkitSpeechRecognition' in window,
    });
  }, []);

  const speak = useCallback((text) => {
    if (muted || !('speechSynthesis' in window)) return;
    window.speechSynthesis.cancel();
    const utt = new SpeechSynthesisUtterance(stripMarkdown(text));
    utt.rate = 0.95; utt.pitch = 1; utt.volume = 1;
    const voices = window.speechSynthesis.getVoices();
    const preferred = voices.find(v => v.lang.startsWith('en') &&
      (v.name.includes('Natural') || v.name.includes('Samantha') || v.name.includes('Google')))
      || voices.find(v => v.lang.startsWith('en'));
    if (preferred) utt.voice = preferred;
    utt.onstart = () => setSpeaking(true);
    utt.onend = () => setSpeaking(false);
    utt.onerror = () => setSpeaking(false);
    window.speechSynthesis.speak(utt);
  }, [muted]);

  const stopSpeaking = useCallback(() => { window.speechSynthesis?.cancel(); setSpeaking(false); }, []);
  const startListening = useCallback(() => {
    if (!supported.stt || listening) return;
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SR();
    recognition.lang = 'en-US'; recognition.continuous = false; recognition.interimResults = false;
    recognition.onstart = () => setListening(true);
    recognition.onresult = (e) => onTranscript(e.results[0][0].transcript);
    recognition.onerror = () => setListening(false);
    recognition.onend = () => setListening(false);
    recognitionRef.current = recognition;
    recognition.start();
  }, [supported.stt, listening, onTranscript]);
  const stopListening = useCallback(() => { recognitionRef.current?.stop(); setListening(false); }, []);

  return { speak, stopSpeaking, startListening, stopListening, speaking, listening, supported };
}

function LoadingDots() {
  return (
    <div style={{ display: 'flex', gap: '4px', alignItems: 'center', padding: '2px 0' }}>
      <style>{`@keyframes cwBounce{0%,60%,100%{transform:translateY(0)}30%{transform:translateY(-5px)}}`}</style>
      {[0, 0.15, 0.3].map((d, i) => (
        <div key={i} style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#a78bfa', animation: `cwBounce 1s infinite ${d}s` }} />
      ))}
    </div>
  );
}

function ConsentModal({ onAccept, onDecline }) {
  return (
    <div style={{
      position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.85)',
      borderRadius: '24px', display: 'flex', alignItems: 'center',
      justifyContent: 'center', padding: '24px', zIndex: 10,
    }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: '32px', marginBottom: '12px' }}>🔒</div>
        <div style={{ fontSize: '14px', fontWeight: '700', color: 'white', marginBottom: '8px' }}>Lab Result Storage Consent</div>
        <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)', lineHeight: '1.6', marginBottom: '16px' }}>
          May we securely store your lab results to help improve care for CKD patients?
          Your data is encrypted, de-identified for research, and never sold.
        </p>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={onDecline} style={{ flex: 1, padding: '10px', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '10px', color: 'white', fontSize: '12px', cursor: 'pointer', fontFamily: 'inherit' }}>No thanks</button>
          <button onClick={onAccept} style={{ flex: 1, padding: '10px', background: 'linear-gradient(135deg, #1A5EA8, #0B2545)', border: 'none', borderRadius: '10px', color: 'white', fontSize: '12px', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit' }}>Yes, I consent ✅</button>
        </div>
      </div>
    </div>
  );
}

const CareCopilotWidget = () => {
  const { isAuthenticated } = useAuth0();
  const { tier, subscribe } = useSubscription();
  const isPremium = PREMIUM_ENABLED ? true : isAuthenticated;
  const location = useLocation();

  // ── Hide widget on full Care Copilot page ──────────────────────────────────
  const isHidden = HIDDEN_ROUTES.some(route => location.pathname.startsWith(route));

  const [open, setOpen] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [windowState, setWindowState] = useState('half'); // 'mini' | 'half' | 'full'
  const [fabExpanded, setFabExpanded] = useState(false);
  const [fabVisible, setFabVisible] = useState(false);
  const fabTimerRef = React.useRef(null);
  const [widgetSize, setWidgetSize] = useState({ width: 360, height: 530 });  
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const [muted, setMuted] = useState(false);
  const [messages, setMessages] = useState([{
    id: 1, role: 'assistant',
    content: PREMIUM_ENABLED
      ? "Hi! I'm **Care Copilot** 🤝\n\nAsk me anything about CKD, your clinical trials, or your lab results. **Type** or use **🎤 mic** to speak.\n\nPremium members can upload lab results 🧪 for AI interpretation."
      : "Hi! I'm **Care Copilot** 🤝\n\nAsk me anything about CKD, your clinical trials, or your lab results. **Type** or use **🎤 mic** to speak.\n\nLog in to upload lab results 🧪 for AI interpretation.",
  }]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [freeCount, setFreeCount] = useState(0);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [unread, setUnread] = useState(0);
  const [showConsent, setShowConsent] = useState(false);
  const [consentGiven, setConsentGiven] = useState(null);
  const [showAIDisclosure, setShowAIDisclosure] = useState(false);
  const [pendingQuestion, setPendingQuestion] = useState(null);
  const [uploadingLab, setUploadingLab] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [trialRequested, setTrialRequested] = useState(false);
  const [requestingTrial, setRequestingTrial] = useState(false);
  const [activeContext, setActiveContext] = useState(CONTEXTS[0]);
  const [appHelperHistory, setAppHelperHistory] = useState([]);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const resizingRef = useRef(null);

  const handleTranscript = useCallback((transcript) => {
    setInput(transcript);
    setTimeout(() => handleSendWithText(transcript), 300);
  }, []); // eslint-disable-line

  const { speak, stopSpeaking, startListening, stopListening, speaking, listening, supported } =
    useVoice({ muted, onTranscript: handleTranscript });

  useEffect(() => { setFreeCount(getSessionCount()); }, []);

  useEffect(() => {
    if (open) { setUnread(0); setTimeout(() => inputRef.current?.focus(), 100); }
    else { stopSpeaking(); if (fullscreen) setFullscreen(false); }
  }, [open, stopSpeaking]);

  useEffect(() => {
    if (open) messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading, open]);

  // Close widget when navigating to hidden route
  // ── FAB pop-up on every login ─────────────────────────────────────────────
  const hasTriggeredFab = React.useRef(false);
  useEffect(() => {
    if (isAuthenticated && !open && !isHidden && !hasTriggeredFab.current) {
      hasTriggeredFab.current = true;
      setTimeout(() => {
        setFabExpanded(true);
        if (fabTimerRef.current) clearTimeout(fabTimerRef.current);
        fabTimerRef.current = setTimeout(() => setFabExpanded(false), 5000);
      }, 1000);
    }
    if (!isAuthenticated) hasTriggeredFab.current = false;
  }, [isAuthenticated]); // eslint-disable-line

  useEffect(() => {
    if (isHidden && open) {
      setOpen(false);
      stopSpeaking();
      stopListening();
      setFullscreen(false);
    }
  }, [isHidden, open, stopSpeaking, stopListening]);

  useEffect(() => {
    if (fullscreen) { document.body.style.overflow = 'hidden'; }
    else { document.body.style.overflow = ''; }
    return () => { document.body.style.overflow = ''; };
  }, [fullscreen]);

  // ── Preloaded prompt from navigation state or custom event ─────────────────
useEffect(() => {
  const handler = (e) => {
    const prompt = e.detail?.prompt;
    if (prompt) {
      setOpen(true);
      setInput(prompt);
      setShowSuggestions(false);
      setTimeout(() => {
        handleSendWithText(prompt);
      }, 800);
    }
  };
  window.addEventListener('copilot-preload', handler);
  return () => window.removeEventListener('copilot-preload', handler);
});  // no dependency array — always fresh reference to handleSendWithText

  const addMessage = useCallback((role, content) => {
    const msg = { id: Date.now() + Math.random(), role, content };
    setMessages(prev => [...prev, msg]);
    if (!open && role === 'assistant') setUnread(u => u + 1);
  }, [open]);

  const handleContextSelect = useCallback((ctx) => {
    setActiveContext(ctx);
    // Add a subtle inline note so user knows context switched
    const noteMsg = { id: Date.now() + Math.random(), role: 'note', content: ctx.systemNote, contextId: ctx.id };
    setMessages(prev => [...prev, noteMsg]);
  }, []);

  // ── Resize handlers ───────────────────────────────────────────────────────
  const handleResizeStart = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startY = e.clientY;
    const startW = widgetSize.width;
    const startH = widgetSize.height;

    const onMouseMove = (e) => {
      const maxW = Math.min(720, window.innerWidth - 48);
      const maxH = Math.min(850, window.innerHeight - 110);
      const newW = Math.min(Math.max(startW + (startX - e.clientX), 300), maxW);
      const newH = Math.min(Math.max(startH + (startY - e.clientY), 400), maxH);
      setWidgetSize({ width: newW, height: newH });
    };
    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'nw-resize';
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [widgetSize]);

  const startDailyCheckIn = useCallback(async () => {
    setActiveContext(CONTEXTS.find((c) => c.id === 'daily-checkin'));
    setOpen(true);
    setFullscreen(true);
    setLoading(true);
    setMessages([]); // clear the generic default welcome — this flow has its own greeting
    try {
      const data = await apiService.sendAppHelperMessage('DAILY_CHECKIN_MODE', []);
      setAppHelperHistory(data.updatedHistory);

      const lastTurn = data.updatedHistory[data.updatedHistory.length - 1];
      const displayText = Array.isArray(lastTurn.content)
        ? lastTurn.content.filter((b) => b.type === 'text').map((b) => b.text).join('\n')
        : lastTurn.content;

      const finalText = displayText || "Hi! Let's get your check-in started.";
      addMessage('assistant', finalText);
      speak(finalText);
    } catch (err) {
      addMessage('assistant', "I couldn't start your check-in just now — try again in a moment, or just ask me anything.");
    } finally {
      setLoading(false);
    }
  }, [addMessage, speak]);

  useEffect(() => {
    const alreadyStarted = sessionStorage.getItem('copilot_daily_checkin_started');
    if (location.pathname === '/dashboard' && isAuthenticated && !alreadyStarted) {
      sessionStorage.setItem('copilot_daily_checkin_started', 'true');
      startDailyCheckIn();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, isAuthenticated]);

  // ── Return null entirely when on hidden route ──────────────────────────────
  if (isHidden) return null;

  const isLimitReached = !isPremium && freeCount >= FREE_LIMIT;
  const remaining = Math.max(0, FREE_LIMIT - freeCount);

  const getEndpoint = () => {
    return isAuthenticated ? '/v1/copilot/ask-sonnet' : '/v1/copilot/ask';
  };

  const handleSendWithText = async (question) => {
    if (!question?.trim() || loading || isLimitReached) return;

    // First-use AI data disclosure gate (App Store Guideline
    // 5.1.1(i)/5.1.2(i)) — hold the message until the user has seen and
    // accepted the disclosure, then send it once they do.
    if (!hasAcceptedAIDisclosure()) {
      setPendingQuestion(question);
      setShowAIDisclosure(true);
      return;
    }

    setInput(''); setShowSuggestions(false);
    addMessage('user', question);
    setLoading(true);

    if (activeContext.id === 'profile' || activeContext.id === 'daily-checkin') {
      try {
        const data = await apiService.sendAppHelperMessage(question, appHelperHistory);
        setAppHelperHistory(data.updatedHistory);

        const lastTurn = data.updatedHistory[data.updatedHistory.length - 1];
        const displayText = Array.isArray(lastTurn.content)
          ? lastTurn.content.filter(b => b.type === 'text').map(b => b.text).join('\n')
          : lastTurn.content;

        const finalText = displayText || "Let's continue — what would you like to share next?";
        addMessage('assistant', finalText);
        speak(finalText);
      } catch (err) {
        const errMsg = "I ran into an issue saving that — let's try again.";
        addMessage('assistant', errMsg);
        speak(errMsg);
      } finally {
        setLoading(false);
      }
      return;
    }

    if (!isPremium) { const c = incrementSessionCount(); setFreeCount(c); }
    try {
      let patientContext = '';
      if (isPremium) {
        try {
          const r = await api.get(`/v1/labs/context?question=${encodeURIComponent(question)}`);
          patientContext = r.data?.context || '';
        } catch (e) { }
      }
      const payload = { question, systemPrompt: activeContext.systemPrompt, contextId: activeContext.id };
      if (isPremium && patientContext) payload.patientContext = patientContext;
      const response = await api.post(getEndpoint(), payload);
      const data = response.data;
      const answer = data.limitReached
        ? (PREMIUM_ENABLED
            ? "You've used your **3 free questions** today!\n\nUpgrade to **Care Copilot Premium** for unlimited questions and lab result interpretation."
            : "You've used your **3 free questions** today!\n\nPlease **log in** for unlimited questions and lab result interpretation.")
        : data.answer || 'I was unable to process that. Please try again.';
      addMessage('assistant', answer);
      speak(answer);
    } catch (err) {
      const errMsg = err.response?.status === 429
        ? (PREMIUM_ENABLED ? "Daily limit reached. **Upgrade to Premium**!" : "Daily limit reached. **Please log in** for unlimited access.")
        : err.response?.status === 403
          ? (PREMIUM_ENABLED ? "This requires **Care Copilot Premium**." : "Please **log in** to continue.")
          : 'Connection error — please try again.';
      addMessage('assistant', errMsg);
      speak(errMsg);
    } finally { setLoading(false); }
  };

  const handleSend = () => handleSendWithText(input);

  const handleMicClick = () => {
    if (listening) stopListening(); else { stopSpeaking(); startListening(); }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    if (!isPremium) {
      const msg = PREMIUM_ENABLED
        ? '🧪 **Lab result interpretation** is a Premium feature.\n\nUpgrade or request a free trial to upload and interpret your lab results!'
        : '🧪 **Lab result interpretation** requires logging in.\n\nPlease log in to upload and interpret your lab results!';
      addMessage('assistant', msg);
      return;
    }
    setPendingFile(file);
    if (consentGiven === null) setShowConsent(true);
    else processLabUpload(file, consentGiven);
  };

  const handleConsentAccept = () => { setConsentGiven(true); setShowConsent(false); if (pendingFile) processLabUpload(pendingFile, true); setPendingFile(null); };
  const handleConsentDecline = () => { setConsentGiven(false); setShowConsent(false); if (pendingFile) processLabUpload(pendingFile, false); setPendingFile(null); };

  const handleAIDisclosureAccept = () => {
    recordAIDisclosureAcceptance();
    setShowAIDisclosure(false);
    const question = pendingQuestion;
    setPendingQuestion(null);
    if (question) handleSendWithText(question);
  };
  const handleAIDisclosureDecline = () => {
    setShowAIDisclosure(false);
    setPendingQuestion(null);
  };

  const processLabUpload = async (file, consent) => {
    setUploadingLab(true); setShowSuggestions(false);
    addMessage('user', `📎 Uploading: ${file.name}`);
    addMessage('assistant', `🧪 Analyzing your lab results from **${file.name}**...\n\nThis may take 15-30 seconds.`);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('consentGiven', consent.toString());
      const response = await api.post('/v1/labs/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000,
      });
      setMessages(prev => prev.filter(m => !m.content.includes('Analyzing your lab results')));
      const interpretation = response.data.interpretation || 'Lab results uploaded successfully.';
      addMessage('assistant', `📋 **Lab Result Interpretation**\n\n${interpretation}`);
      speak(interpretation);
    } catch (err) {
      setMessages(prev => prev.filter(m => !m.content.includes('Analyzing your lab results')));
      addMessage('assistant', '❌ Failed to process lab results. Please try again with a clear PDF or image.');
    } finally { 
      setUploadingLab(false); 
      setPendingFile(null); 
    }
  };

  const handleRequestTrial = async () => {
    if (!isAuthenticated) { addMessage('assistant', 'Please **sign in** first to request a free trial.'); return; }
    setRequestingTrial(true);
    try {
      await api.post('/v1/trial-request/request');
      setTrialRequested(true);
      addMessage('assistant', "✅ **Free trial requested!**\n\nWe've received your request and will activate your 7-day Care Copilot Premium trial within 24 hours.\n\nYou'll receive a confirmation email shortly. Thank you for being part of NephroTrax! 🤝");
    } catch (err) {
      addMessage('assistant', '❌ Failed to submit request. Please email **support@nephrotrax.com (or sandeep.sinha@verainsight.com)** directly.');
    } finally { setRequestingTrial(false); }
  };

  const profileSuggestions = ["Let's fill in my profile", "I want to update something"];

  const suggestions = activeContext.id === 'profile'
    ? profileSuggestions
    : isPremium ? [
        "What do my latest lab results mean?",
        "What CKD stage am I at?",
        "Is my kidney function getting worse?",
        "What should I ask my nephrologist?",
      ] : [
        "Why don't I see matched trials?",
        "What is diabetic nephropathy?",
        "What does eGFR mean?",
        "How do I book travel to a trial?",
      ];

  const tierLabel = PREMIUM_ENABLED
    ? (isPremium ? 'PREMIUM' : `${remaining} FREE`)
    : (isPremium ? '' : `${remaining} FREE`);
  const tierColor = isPremium ? 'linear-gradient(135deg, #f59e0b, #d97706)' : 'rgba(102,126,234,0.2)';

  return (
    <>
      <style>{`
        .cw-input::placeholder{color:rgba(255,255,255,0.3);}
        .cw-input:focus{border-color:rgba(102,126,234,0.6)!important;outline:none;}
        .cw-suggestion:hover{background:rgba(102,126,234,0.25)!important;}
        .cw-send:hover:not(:disabled){transform:scale(1.05);}
        .cw-fab:hover{transform:scale(1.08);}
        .cw-icon-btn:hover{background:rgba(255,255,255,0.15)!important;}
        .cw-msg-speak:hover{opacity:1!important;}
        .cw-widget{animation:cwSlideUp 0.3s cubic-bezier(0.34,1.56,0.64,1);}
        @keyframes cwSlideUp{from{opacity:0;transform:translateY(20px) scale(0.95);}to{opacity:1;transform:translateY(0) scale(1);}}
        .cw-messages::-webkit-scrollbar{width:4px;}
        .cw-messages::-webkit-scrollbar-track{background:transparent;}
        .cw-messages::-webkit-scrollbar-thumb{background:rgba(255,255,255,0.15);border-radius:4px;}
        @keyframes cwPulse{0%,100%{box-shadow:0 0 0 0 rgba(239,68,68,0.5);}50%{box-shadow:0 0 0 8px rgba(239,68,68,0);}}
        @keyframes cwSpeakPulse{0%,100%{box-shadow:0 0 0 0 rgba(102,126,234,0.5);}50%{box-shadow:0 0 0 6px rgba(102,126,234,0);}}
        @keyframes cwSpin{from{transform:rotate(0deg);}to{transform:rotate(360deg);}}
        @keyframes cwFabPulse{0%,100%{box-shadow:0 8px 32px rgba(102,126,234,0.55),0 0 0 0 rgba(102,126,234,0.4);}50%{box-shadow:0 8px 32px rgba(102,126,234,0.7),0 0 0 10px rgba(102,126,234,0);}}
        @keyframes cwFabLabel{from{opacity:0;transform:translateX(8px);}to{opacity:1;transform:translateX(0);}}
        @keyframes cwFabSlideUp{from{opacity:0;transform:translateY(80px);}to{opacity:1;transform:translateY(0);}}
        .cw-trial-btn:hover{background:linear-gradient(135deg,#1A5EA8,#0B2545)!important;color:white!important;}
        .cw-resize-handle:hover svg circle{opacity:.6!important;}
        @media (max-width: 600px) {
          .cw-context-indicator { display: none !important; }
          .cw-fullscreen-sidebar { display: none !important; }
        }
      `}</style>
      {open && (
        <div
          onClick={() => {
            setOpen(false); stopSpeaking(); stopListening(); setFullscreen(false); setWindowState('half');
            setFabExpanded(true);
            if (fabTimerRef.current) clearTimeout(fabTimerRef.current);
            fabTimerRef.current = setTimeout(() => setFabExpanded(false), 4000);
          }}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: fullscreen ? 99998 : 9999,
            background: 'transparent',
          }}
        />
      )}
      {open && (
        <div className="cw-widget" style={{
          position: 'fixed',
          bottom: fullscreen ? 0 : '90px',
          right: fullscreen ? 0 : '24px',
          top: fullscreen ? 0 : 'auto',
          left: fullscreen ? 0 : 'auto',
          width: fullscreen ? '100vw' : `${widgetSize.width}px`,
          height: fullscreen ? '100vh' : `${widgetSize.height}px`,
          maxWidth: fullscreen ? '100vw' : 'calc(100vw - 48px)',
          maxHeight: fullscreen ? '100vh' : 'calc(100vh - 110px)',
          borderRadius: fullscreen ? 0 : '16px',
          zIndex: fullscreen ? 99999 : 10000,
          background: 'linear-gradient(160deg, #0D2B45 0%, #1a3a5c 60%, #2d1b69 100%)',
          border: fullscreen ? 'none' : '1px solid rgba(255,255,255,0.12)',
          boxShadow: fullscreen ? 'none' : '0 24px 80px rgba(0,0,0,0.5)',
          display: 'flex', flexDirection: fullscreen ? 'row' : 'column',
          overflow: 'hidden', position: 'fixed',
          fontFamily: "'DM Sans', system-ui, sans-serif",
          transition: fullscreen ? 'none' : 'width 0.15s ease, height 0.15s ease',
        }}>
          {showAIDisclosure && <AIDisclosureModal onAccept={handleAIDisclosureAccept} onDecline={handleAIDisclosureDecline} />}
          {showConsent && <ConsentModal onAccept={handleConsentAccept} onDecline={handleConsentDecline} />}

          {/* ── Resize handle (top-left corner, hidden in fullscreen) ── */}
          {!fullscreen && !isMobile && (
            <div
              onMouseDown={handleResizeStart}
              style={{
                position:'absolute', top:0, left:0,
                width:20, height:20,
                cursor:'nw-resize',
                zIndex:20,
              }}
            >
              <svg width="12" height="12" viewBox="0 0 12 12" style={{ position:'absolute', top:4, left:4, opacity:.3, pointerEvents:'none' }}>
                <circle cx="2" cy="2" r="1.2" fill="white"/>
                <circle cx="6" cy="2" r="1.2" fill="white"/>
                <circle cx="2" cy="6" r="1.2" fill="white"/>
                <circle cx="10" cy="2" r="1.2" fill="white"/>
                <circle cx="6" cy="6" r="1.2" fill="white"/>
                <circle cx="2" cy="10" r="1.2" fill="white"/>
              </svg>
            </div>
          )}

          {/* ── Fullscreen Left Context Sidebar ── */}
          {fullscreen && (
            <div className="cw-fullscreen-sidebar" style={{
              width: 80, flexShrink: 0,
              display: 'flex', flexDirection: 'column', gap: 6,
              padding: '14px 8px',
              background: 'rgba(0,0,0,.18)',
              borderRight: '1px solid rgba(255,255,255,.06)',
              overflowY: 'auto',
            }}>
              <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:9, fontWeight:700, letterSpacing:'.1em', textTransform:'uppercase', color:'rgba(255,255,255,.3)', textAlign:'center', margin:'0 0 8px' }}>Focus</p>
              {CONTEXTS.map((ctx, i) => (
                <button
                  key={ctx.id}
                  onClick={() => handleContextSelect(ctx)}
                  title={ctx.tooltip}
                  style={{
                    display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
                    gap:4, width:'100%', padding:'9px 4px', borderRadius:11,
                    border: activeContext.id === ctx.id ? `1.5px solid ${ctx.color}55` : '1.5px solid transparent',
                    background: activeContext.id === ctx.id ? 'rgba(255,255,255,.1)' : 'rgba(255,255,255,.04)',
                    cursor:'pointer',
                    boxShadow: activeContext.id === ctx.id ? `0 0 10px ${ctx.color}28` : 'none',
                    transition:'all .2s',
                    position:'relative',
                  }}
                >
                  <span style={{ fontSize:19, lineHeight:1 }}>{ctx.icon}</span>
                  <span style={{
                    fontSize:9, fontWeight:600, letterSpacing:'.03em', textAlign:'center',
                    lineHeight:1.2, maxWidth:62, wordWrap:'break-word',
                    color: activeContext.id === ctx.id ? ctx.color : 'rgba(255,255,255,.55)',
                  }}>{ctx.label}</span>
                  {activeContext.id === ctx.id && (
                    <div style={{ position:'absolute', top:5, right:5, width:5, height:5, borderRadius:'50%', background:ctx.color }} />
                  )}
                </button>
              ))}
            </div>
          )}

          {/* ── Main chat column ── */}
          <div style={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden' }}>

          {/* Header */}
          <div style={{ padding: fullscreen ? '16px 24px' : '14px 16px', background: 'rgba(0,0,0,0.25)', borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
            <div style={{ width: fullscreen ? '44px' : '36px', height: fullscreen ? '44px' : '36px', borderRadius: '10px', flexShrink: 0, background: 'linear-gradient(135deg, #1A5EA8, #0B2545)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: fullscreen ? '22px' : '18px', animation: speaking ? 'cwSpeakPulse 1s infinite' : 'none', boxShadow: '0 4px 12px rgba(102,126,234,0.4)' }}>🤝</div>
            <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
              <div style={{ fontSize: fullscreen ? '18px' : '14px', fontWeight: '700', color: 'white', lineHeight: 1.2 }}>Care Copilot</div>
              <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.45)', display: 'flex', alignItems: 'center', gap: '5px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: listening ? '#ef4444' : uploadingLab ? '#f59e0b' : speaking ? '#1A5EA8' : '#10b981', animation: (listening || speaking || uploadingLab) ? 'cwPulse 1s infinite' : 'none' }} />
                {listening ? '🎤 Listening...' : uploadingLab ? '🧪 Analyzing labs...' : speaking ? '🔊 Speaking...' : loading ? 'Thinking...' : isPremium ? (PREMIUM_ENABLED ? 'Premium · AI-powered' : 'AI-powered') : 'Online · Free tier'}
              </div>
            </div>
            {tierLabel && (
              <div style={{ fontSize: '10px', fontWeight: '700', letterSpacing: '0.08em', textTransform: 'uppercase', padding: '3px 8px', borderRadius: '10px', background: tierColor, border: !isPremium ? '1px solid rgba(102,126,234,0.4)' : 'none', color: isPremium ? 'white' : '#a78bfa', flexShrink: 0 }}>{tierLabel}</div>
            )}
            {/* Active context indicator — fullscreen only */}
            {fullscreen && (
              <div  className="cw-context-indicator" style={{ display:'flex', alignItems:'center', gap:5, padding:'3px 10px', borderRadius:999, background:`${activeContext.color}18`, border:`1px solid ${activeContext.color}35`, flexShrink:0 }}>
                <span style={{ fontSize:12 }}>{activeContext.icon}</span>
                <span style={{ fontSize:11, fontWeight:600, color:activeContext.color }}>{activeContext.label}</span>
              </div>
            )}
            <button className="cw-icon-btn" onClick={() => { setMuted(m => !m); if (!muted) stopSpeaking(); }} style={{ background: muted ? 'rgba(239,68,68,0.2)' : 'rgba(255,255,255,0.08)', border: muted ? '1px solid rgba(239,68,68,0.4)' : 'none', borderRadius: '8px' , flexShrink: 0, color: muted ? '#fca5a5' : 'rgba(255,255,255,0.6)', cursor: 'pointer', fontSize: '14px', padding: '6px 8px', lineHeight: 1, transition: 'all 0.2s' }}>{muted ? '🔇' : '🔊'}</button>
            <SourcesPanel contextId={activeContext.id} label="" />
            {/* Expand/Restore button */}
            {fullscreen ? (
              !isMobile && <button className="cw-icon-btn" title="Restore window" onClick={() => { setFullscreen(false); setWindowState('half'); }} style={{ background:'rgba(255,255,255,0.08)', border:'none', borderRadius:'8px', color:'rgba(255,255,255,0.6)', cursor:'pointer', fontSize:'14px', padding:'6px 8px', lineHeight:1, flexShrink:0, transition:'all 0.2s' }}>❚❚</button>
            ) : (
              <button className="cw-icon-btn" title="Expand to full page" onClick={() => { setFullscreen(true); setWindowState('full'); }} style={{ background:'rgba(255,255,255,0.08)', border:'none', borderRadius:'8px', color:'rgba(255,255,255,0.6)', cursor:'pointer', fontSize:'14px', padding:'6px 8px', lineHeight:1, flexShrink:0, transition:'all 0.2s' }}>□</button>
            )}
            {/* Close → minimize to FAB */}
            <button className="cw-icon-btn" title="Minimize" onClick={() => {
              setOpen(false); stopSpeaking(); stopListening(); setFullscreen(false); setWindowState('half');
              setFabExpanded(true);
              if (fabTimerRef.current) clearTimeout(fabTimerRef.current);
              fabTimerRef.current = setTimeout(() => setFabExpanded(false), 4000);
            }} style={{ background:'rgba(255,255,255,0.08)', border:'none', borderRadius:'8px', flexShrink: 0, color:'rgba(255,255,255,0.6)', cursor:'pointer', fontSize:'16px', padding:'6px 9px', lineHeight:1, transition:'all 0.2s' }}>×</button>
          </div>

          {/* ── Mobile context row (visible when fullscreen on small screens) ── */}
          {(!fullscreen || isMobile) && (
            <div style={{
    display: 'flex', gap: '6px', padding: '8px 12px',
    overflowX: 'auto', borderBottom: '1px solid rgba(255,255,255,0.06)',
    flexShrink: 0, WebkitOverflowScrolling: 'touch',
  }}>
              {CONTEXTS.map(ctx => (
      <button
        key={ctx.id}
        onClick={() => handleContextSelect(ctx)}
        style={{
          flexShrink: 0, display: 'flex', alignItems: 'center', gap: 5,
          padding: '6px 12px', borderRadius: 999, whiteSpace: 'nowrap',
          border: activeContext.id === ctx.id ? `1px solid ${ctx.color}55` : '1px solid rgba(255,255,255,0.1)',
          background: activeContext.id === ctx.id ? `${ctx.color}18` : 'rgba(255,255,255,0.05)',
          color: activeContext.id === ctx.id ? ctx.color : 'rgba(255,255,255,0.6)',
          fontSize: 12, fontWeight: 600, cursor: 'pointer',
        }}
      >
        <span>{ctx.icon}</span><span>{ctx.label}</span>
      </button>
    ))}
            </div>
          )}

          {/* Messages */}
          <div className="cw-messages" style={{ flex: 1, overflowY: 'auto', padding: fullscreen ? '24px max(24px, calc(50vw - 400px))' : '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {messages.map(msg => {
              const isUser = msg.role === 'user';
              const isNote = msg.role === 'note';

              // Context switch note badge
              if (isNote) {
                const ctx = CONTEXTS.find(c => c.id === msg.contextId);
                return (
                  <div key={msg.id} style={{ display:'flex', justifyContent:'center', margin:'4px 0' }}>
                    <span style={{ display:'inline-flex', alignItems:'center', gap:5, padding:'5px 12px', borderRadius:999, fontSize:11, fontWeight:600, background: ctx ? `${ctx.color}18` : 'rgba(255,255,255,.08)', color: ctx ? ctx.color : 'rgba(255,255,255,.5)', border:`1px solid ${ctx ? ctx.color+'35' : 'rgba(255,255,255,.12)'}` }}>
                      {ctx?.icon} {msg.content}
                    </span>
                  </div>
                );
              }
              return (
                <div key={msg.id} style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', flexDirection: isUser ? 'row-reverse' : 'row', maxWidth: fullscreen ? '800px' : 'none', margin: fullscreen ? '0 auto' : '0', width: fullscreen ? '100%' : 'auto' }}>
                  <div style={{ width: fullscreen ? '36px' : '28px', height: fullscreen ? '36px' : '28px', borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: fullscreen ? '16px' : '13px', background: isUser ? 'linear-gradient(135deg, #1A5EA8, #0B2545)' : 'rgba(255,255,255,0.08)', border: isUser ? 'none' : '1px solid rgba(255,255,255,0.12)' }}>{isUser ? '👤' : '🤝'}</div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: isUser ? 'flex-end' : 'flex-start', gap: '4px', maxWidth: '80%' }}>
                    <div style={{ padding: fullscreen ? '14px 20px' : '10px 14px', fontSize: fullscreen ? '15px' : '13px', lineHeight: '1.7', borderRadius: isUser ? '16px 4px 16px 16px' : '4px 16px 16px 16px', background: isUser ? 'linear-gradient(135deg, #1A5EA8, #0B2545)' : 'rgba(255,255,255,0.07)', color: isUser ? 'white' : 'rgba(255,255,255,0.88)', border: isUser ? 'none' : '1px solid rgba(255,255,255,0.09)', boxShadow: isUser ? '0 2px 12px rgba(102,126,234,0.3)' : 'none' }} dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.content) }} />
                    {!isUser && supported.tts && (<button className="cw-msg-speak" onClick={() => { if (speaking) stopSpeaking(); else speak(msg.content); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', color: 'rgba(255,255,255,0.35)', padding: '2px 6px', borderRadius: '6px', opacity: 0.6, transition: 'all 0.2s' }}>{speaking ? '⏹ stop' : '🔊 listen'}</button>)}
                  </div>
                </div>
              );
            })}
            {loading && (
              <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', maxWidth: fullscreen ? '800px' : 'none', margin: fullscreen ? '0 auto' : '0', width: fullscreen ? '100%' : 'auto' }}>
                <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px' }}>🤝</div>
                <div style={{ padding: '10px 14px', borderRadius: '4px 16px 16px 16px', background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.09)' }}><LoadingDots /></div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Suggestions */}
          {showSuggestions && !isLimitReached && (
            <div style={{ padding: '0 16px 10px', display: 'flex', flexWrap: 'wrap', gap: '6px', justifyContent: fullscreen ? 'center' : 'flex-start' }}>
              {suggestions.map((s, i) => (
                <button key={i} className="cw-suggestion" onClick={() => handleSendWithText(s)} style={{ background: 'rgba(102,126,234,0.12)', border: '1px solid rgba(102,126,234,0.25)', borderRadius: '16px', padding: '6px 14px', fontSize: fullscreen ? '13px' : '11px', fontWeight: '500', color: '#c4b5fd', cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.2s' }}>{s}</button>
              ))}
            </div>
          )}

          {/* Upgrade banner */}
          {PREMIUM_ENABLED && isLimitReached && !isPremium && (
            <div style={{ margin: '0 12px 10px', background: 'linear-gradient(135deg, rgba(102,126,234,0.2), rgba(118,75,162,0.2))', border: '1px solid rgba(102,126,234,0.35)', borderRadius: '14px', padding: '14px 16px' }}>
              <div style={{ fontSize: '12px', fontWeight: '700', color: 'white', marginBottom: '10px' }}>✨ Unlock Care Copilot Premium</div>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                <button onClick={() => subscribe('monthly')} style={{ flex: 1, padding: '8px', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px', color: 'white', fontSize: '12px', cursor: 'pointer', fontFamily: 'inherit' }}>$19/mo</button>
                <button onClick={() => subscribe('yearly')} style={{ flex: 1, padding: '8px', background: 'linear-gradient(135deg, #1A5EA8, #0B2545)', border: 'none', borderRadius: '8px', color: 'white', fontSize: '12px', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit' }}>$99/yr ⭐</button>
              </div>
              {!trialRequested ? (
                <button className="cw-trial-btn" onClick={handleRequestTrial} disabled={requestingTrial} style={{ width: '100%', padding: '8px', background: 'rgba(255,255,255,0.05)', border: '1px dashed rgba(255,255,255,0.3)', borderRadius: '8px', color: 'rgba(255,255,255,0.7)', fontSize: '12px', cursor: requestingTrial ? 'not-allowed' : 'pointer', fontFamily: 'inherit', transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                  {requestingTrial ? (<><div style={{ width: '10px', height: '10px', borderRadius: '50%', border: '2px solid white', borderTopColor: 'transparent', animation: 'cwSpin 0.8s linear infinite' }} /> Submitting...</>) : '🎁 Request Free 7-Day Trial'}
                </button>
              ) : (
                <div style={{ textAlign: 'center', fontSize: '12px', color: '#10b981', padding: '6px' }}>✅ Trial requested! Check your email within 24 hours.</div>
              )}
              <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', textAlign: 'center', marginTop: '6px' }}>No credit card required for trial</div>
            </div>
          )}
          {!PREMIUM_ENABLED && isLimitReached && !isPremium && (
            <div style={{ margin: '0 12px 10px', background: 'rgba(102,126,234,0.12)', border: '1px solid rgba(102,126,234,0.3)', borderRadius: '14px', padding: '14px 16px', textAlign: 'center' }}>
              <div style={{ fontSize: '12px', fontWeight: '700', color: 'white', marginBottom: '8px' }}>🔒 Log in for unlimited questions</div>
              <p style={{ fontSize: '11px', color: 'rgba(255,255,255,0.6)', margin: 0 }}>
                You've used your 3 free questions. Log in to continue asking Care Copilot anything.
              </p>
            </div>
          )}

          {/* Input area */}
          <div style={{ padding: fullscreen ? '16px max(24px, calc(50vw - 400px)) 20px' : '10px 12px 14px', borderTop: '1px solid rgba(255,255,255,0.06)', background: 'rgba(0,0,0,0.15)', flexShrink: 0 }}>
            {isPremium && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', padding: '6px 10px', background: 'rgba(102,126,234,0.1)', borderRadius: '10px', border: '1px solid rgba(102,126,234,0.2)', maxWidth: fullscreen ? '800px' : 'none', margin: fullscreen ? '0 auto 8px' : '0 0 8px' }}>
                <span style={{ fontSize: '14px' }}>🧪</span>
                <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.6)', flex: 1 }}>Upload lab results for AI interpretation</span>
                <button onClick={() => fileInputRef.current?.click()} disabled={uploadingLab} style={{ background: 'rgba(102,126,234,0.2)', border: '1px solid rgba(102,126,234,0.4)', borderRadius: '8px', color: '#a78bfa', cursor: uploadingLab ? 'not-allowed' : 'pointer', fontSize: '11px', fontWeight: '600', padding: '4px 10px', fontFamily: 'inherit', transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  {uploadingLab ? (<><div style={{ width: '10px', height: '10px', borderRadius: '50%', border: '2px solid #a78bfa', borderTopColor: 'transparent', animation: 'cwSpin 0.8s linear infinite' }} />Analyzing...</>) : 'Upload PDF/Image'}
                </button>
                <input ref={fileInputRef} type="file" accept=".pdf,image/*" style={{ display: 'none' }} onChange={handleFileSelect} />
              </div>
            )}
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', maxWidth: fullscreen ? '800px' : 'none', margin: fullscreen ? '0 auto' : '0' }}>
              {supported.stt && (<button onClick={handleMicClick} disabled={isLimitReached || loading} style={{ width: '40px', height: '40px', borderRadius: '12px', flexShrink: 0, border: listening ? '1px solid rgba(239,68,68,0.6)' : '1px solid rgba(255,255,255,0.1)', background: listening ? 'rgba(239,68,68,0.3)' : 'rgba(255,255,255,0.08)', color: listening ? '#fca5a5' : 'rgba(255,255,255,0.6)', cursor: (isLimitReached || loading) ? 'not-allowed' : 'pointer', fontSize: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center', animation: listening ? 'cwPulse 1s infinite' : 'none', transition: 'all 0.2s' }}>{listening ? '⏹' : '🎤'}</button>)}
              <input ref={inputRef} className="cw-input" type="text" value={input} onChange={e => setInput(e.target.value)} onKeyPress={e => { if (e.key === 'Enter') handleSend(); }} placeholder={listening ? 'Listening... speak now' : isLimitReached ? (PREMIUM_ENABLED ? 'Upgrade for unlimited questions...' : 'Log in for unlimited questions...') : fullscreen ? `Ask about ${activeContext.tooltip.toLowerCase()}...` : isPremium ? 'Ask anything or upload labs 🧪...' : supported.stt ? 'Type or 🎤 speak...' : 'Ask about CKD...'} disabled={isLimitReached || loading || uploadingLab} style={{ flex: 1, padding: '10px 14px', background: listening ? 'rgba(239,68,68,0.1)' : 'rgba(255,255,255,0.07)', border: listening ? '1px solid rgba(239,68,68,0.4)' : '1px solid rgba(255,255,255,0.12)', borderRadius: '12px', color: 'white', fontSize: fullscreen ? '15px' : '13px', fontFamily: 'inherit', opacity: (isLimitReached || uploadingLab) ? 0.5 : 1, cursor: (isLimitReached || uploadingLab) ? 'not-allowed' : 'text', transition: 'all 0.2s' }} />
              <button className="cw-send" onClick={handleSend} disabled={!input.trim() || loading || isLimitReached || uploadingLab} style={{ width: '40px', height: '40px', borderRadius: '12px', border: 'none', flexShrink: 0, background: (!input.trim() || loading || isLimitReached || uploadingLab) ? 'rgba(255,255,255,0.08)' : 'linear-gradient(135deg, #1A5EA8, #0B2545)', color: (!input.trim() || loading || isLimitReached || uploadingLab) ? 'rgba(255,255,255,0.3)' : 'white', fontSize: '18px', cursor: (!input.trim() || loading || isLimitReached || uploadingLab) ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s', boxShadow: (!input.trim() || loading || isLimitReached || uploadingLab) ? 'none' : '0 2px 12px rgba(102,126,234,0.4)' }}>↑</button>
            </div>
            <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.25)', textAlign: 'center', marginTop: '6px' }}>
              {isPremium ? (PREMIUM_ENABLED ? '✨ Premium · Unlimited AI · Lab interpretation · Profile-aware' : '✨ Unlimited AI · Lab interpretation · Profile-aware') : `🎤 speak · 🔊 auto-read ${muted ? '(muted)' : ''}`} · ⚕️ educational only
            </div>
          </div>
          </div>{/* end main chat column */}
        </div>
      )}{/* end widget */}

      {/* Animated FAB — expands on login + hover */}
      {!open && (
        <button
          onClick={() => { setOpen(true); if (isMobile) setFullscreen(true); setFabExpanded(false); if (fabTimerRef.current) clearTimeout(fabTimerRef.current); }}
          onMouseEnter={() => setFabExpanded(true)}
          onMouseLeave={() => setFabExpanded(false)}
          style={{
            position:'fixed', bottom:'24px', right:'24px', zIndex:10000,
            height:'60px',
            width: fabExpanded ? '160px' : '60px',
            borderRadius: fabExpanded ? '30px' : '50%',
            border:'none',
            background: speaking ? 'linear-gradient(135deg,#10b981,#059669)' : 'linear-gradient(135deg,#1A5EA8,#0B2545)',
            color:'white', cursor:'pointer',
            display:'flex', alignItems:'center', justifyContent:'center', gap: fabExpanded ? '10px' : '0',
            boxShadow: speaking ? '0 8px 32px rgba(16,185,129,0.6)' : '0 8px 32px rgba(102,126,234,0.55)',
            transition:'width 0.35s cubic-bezier(0.34,1.56,0.64,1), border-radius 0.35s ease, box-shadow 0.2s',
            outline:'none', overflow:'hidden',
            padding: fabExpanded ? '0 20px' : '0',
            animation: speaking ? 'cwSpeakPulse 1s infinite' : fabExpanded ? 'cwFabPulse 2s ease 0.5s infinite' : 'none',
          }}
        >
          <span style={{ fontSize:'24px', lineHeight:1, flexShrink:0 }}>🤝</span>
          {fabExpanded && (
            <span style={{ fontSize:'15px', fontWeight:'700', whiteSpace:'nowrap', letterSpacing:'0.02em', animation:'cwFabLabel 0.3s ease both' }}>
              Ask Me
            </span>
          )}
          {!fabExpanded && unread > 0 && (
            <div style={{ position:'absolute', top:0, right:0, width:'20px', height:'20px', borderRadius:'50%', background:'#ef4444', color:'white', fontSize:'11px', fontWeight:'700', display:'flex', alignItems:'center', justifyContent:'center', border:'2px solid white' }}>
              {unread}
            </div>
          )}
        </button>
      )}
    </>
  );
};

export default CareCopilotWidget;